import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  AgGridComponentFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {CellSelectComponent} from "../../../../../shared/components/cell-select/cell-select.component";
import {
  AgGridTemplateFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-template-framework/ag-grid-template-framework.component";
import {Serializer} from "../../../../../shared/interfaces/serializer";
import {SelectOption} from "../../../../../shared/interfaces/form";
import {Visibility} from "../../../../../shared/interfaces/settings";
import {TranslateService} from "@ngx-translate/core";
import {AppService} from "../../../../../shared/services/app.service";
import {SettingsService} from "../../../../../shared/services/settings.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../../shared/store/store";
import {Observable, withLatestFrom} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {
  DataSetConditionalDimension,
} from '../../../../../shared/interfaces/dimensions';
import {DialogConfirmComponent} from "../../../../../shared/components/dialog/dialog-confirm/dialog-confirm.component";
import {
  ManageConditionalDimensionDialogComponent
} from "../manage-conditional-dimension-dialog/manage-conditional-dimension-dialog.component";
import {dialogOpen} from "../../../../../shared/store/dialog/dialog.actions";
import {
  selectDataSetConditionalDimensionComponentObjects,
  selectDataSetConditionalDimensionsLoading
} from '../../../../../shared/store/dataSetConditionalDimensions/dataSetConditionalDimensions.selectors';
import {
  selectDataSetDimensionsGroupComponentObjects,
  selectDataSetDimensionsGroupsLoaded
} from '../../../../../shared/store/dataSetDimensionsGroups/dataSetDimensionsGroups.selectors';
import {
  deleteDataSetConditionalDimension,
  deleteDataSetConditionalDimensionSuccess,
  updateDataSetConditionalDimension
} from '../../../../../shared/store/dataSetConditionalDimensions/dataSetConditionalDimensions.actions';
import {
  DatasetDimensionsGroupComponentObject,
  DatasetDimensionsGroupComponentObjects
} from '../../../../../shared/classes/dataset-dimensions-group-component-object';
import {
  DatasetConditionalDimensionComponentObject,
  DatasetConditionalDimensionComponentObjects
} from '../../../../../shared/classes/dataset-conditional-dimension-component-object';

@Component({
  selector: 'app-dimensions-conditional-table',
  templateUrl: './dimensions-conditional-table.component.html',
  styleUrls: ['./dimensions-conditional-table.component.scss']
})
export class DimensionsConditionalTableComponent implements OnInit {
  @ViewChild('dimension') private readonly _nameT: TemplateRef<any>;
  @ViewChild('conditionalDimensionActions') private readonly _conditionalDimensionActionsT: TemplateRef<any>;

  public readonly defaultColDef: any = {
    resizable: true,
    suppressNavigable: true,
    suppressCellSelection: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset']
    }
  };

  public readonly columnDefs$: Observable<Array<any>> = this._store.select(selectDataSetDimensionsGroupComponentObjects)
    .pipe(
      withLatestFrom(this._store.select(selectDataSetDimensionsGroupsLoaded)),
      filter(([datasetDimensionsGroups, loaded]: [DatasetDimensionsGroupComponentObjects, boolean]): boolean => loaded),
      map(([datasetDimensionsGroups, loaded]: [DatasetDimensionsGroupComponentObjects, boolean]) => {
        return [
          {
            minWidth: 200,
            sortable: true,
            filter: 'agTextColumnFilter',
            lockPosition: true,
            headerValueGetter: _ => this._translateS.instant('settings.dimension'),
            valueGetter: params => params.data.payload.attributes.name,
            cellRendererFramework: AgGridTemplateFrameworkComponent,
            cellRendererParams: _ => {
              return {
                ngTemplate: this._nameT
              };
            }
          },
          {
            minWidth: 200,
            sortable: true,
            filter: true,
            lockPosition: true,
            tooltipComponent: 'customTooltip',
            headerTooltip: this._translateS.instant('settings.visibility_tooltip'),
            headerValueGetter: _ => this._translateS.instant('settings.visibility'),
            valueGetter: params => params.data.payload.attributes.visibility,
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component:  CellSelectComponent,
                componentParams: this._getConditionalVisibilityParams()
              };
            },
            filterValueGetter: params => this._translateS.instant(`settings.${params.data.payload.attributes.visibility}`)
          },
          {
            minWidth: 200,
            sortable: true,
            filter: true,
            lockPosition: true,
            filterValueGetter: params => params.data.payload.relationships.data_set_dimensions_group.data?.attributes.name || this._translateS.instant('settings.source_menu_only'),
            valueGetter: params => params.data.payload.relationships.data_set_dimensions_group.data?.attributes.name,
            headerValueGetter: _ => this._translateS.instant('settings.category'),
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component:  CellSelectComponent,
                componentParams: this._getConditionalCategoryParams(datasetDimensionsGroups)
              };
            }
          },
          {
            minWidth: 200,
            sortable: false,
            filter: false,
            lockPosition: true,
            suppressMenu: true,
            cellRendererFramework: AgGridTemplateFrameworkComponent,
            cellRendererParams: _ => {
              return {
                ngTemplate: this._conditionalDimensionActionsT
              };
            }
          }
        ];
      })
    );

  public readonly dimensions$: Observable<DatasetConditionalDimensionComponentObjects> = this._store.select(selectDataSetConditionalDimensionComponentObjects);
  public readonly loading$: Observable<boolean> = this._store.select(selectDataSetConditionalDimensionsLoading);

  constructor(
    private readonly _translateS: TranslateService,
    private readonly _appS: AppService,
    private readonly _settingsS: SettingsService,
    private readonly _store: Store<AppState>
  ) { }

  ngOnInit(): void {
  }

  private _getConditionalVisibilityParams(): any {
    return {
      options:              this._settingsS.visibilities,
      disabled:             this._appS.isNotDatasetAdmin,
      defaultValueGetter:   (dimension: DatasetConditionalDimensionComponentObject) => dimension.payload.attributes.visibility,
      textGetter:           (visibility: SelectOption<Visibility, string>) => visibility.text,
      valueGetter:          (visibility: SelectOption<Visibility, string>) => visibility.key,
      onValueChanges:       (row: any, value: any) => this._updateConditionalDimension(row, { visibility: value })
    };
  }

  private _getConditionalCategoryParams(dimensionCategories: DatasetDimensionsGroupComponentObjects): any {
    return {
      options:            dimensionCategories,
      disabled:           this._appS.isNotDatasetAdmin,
      defaultValueGetter: (dimension: DatasetConditionalDimensionComponentObject) => dimension.payload.relationships.data_set_dimensions_group.data.id,
      textGetter:         (category: DatasetDimensionsGroupComponentObject) => category.payload.attributes.name,
      valueGetter:        (category: DatasetDimensionsGroupComponentObject) => category.payload.id,
      onValueChanges:     (row: any, value: any) => this._updateConditionalDimension(row, { data_set_dimensions_group_id: value })
    };
  }

  public _updateConditionalDimension(dimension: DatasetConditionalDimensionComponentObject, update: Partial<DataSetConditionalDimension>): void {
    this._store.dispatch(updateDataSetConditionalDimension({
      dimension: dimension.payload as Serializer<DataSetConditionalDimension>,
      update: update
    }));
  }

  public onUpdate(dimension: DatasetConditionalDimensionComponentObject): void {
    this._store.dispatch(dialogOpen({
      component: ManageConditionalDimensionDialogComponent,
      config: {
        width: '1000px',
        height: 'auto'
      },
      data: dimension
    }));
  }

  public onDelete(dimension: DatasetConditionalDimensionComponentObject): void {
    this._store.dispatch(dialogOpen({
      component: DialogConfirmComponent,
      config: {
        width: '500px',
        height: 'auto'
      },
      data: {
        title: 'dimensions.conditional_dimensions.delete_title',
        message: 'dimensions.conditional_dimensions.delete_message',
        type: 'delete',
        item: dimension.payload,
        startActions: [deleteDataSetConditionalDimension],
        successActions: [deleteDataSetConditionalDimensionSuccess],
        onSubmit: () => {
          this._store.dispatch(deleteDataSetConditionalDimension({
            dimension: dimension.payload as Serializer<DataSetConditionalDimension>
          }));
        }
      }
    }));
  }

}
